import { useState, useCallback, useEffect, useReducer } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useAuthentication } from 'src/features/authentication/context';
import { useSiteTags } from 'src/hooks/useSiteTags';
import { SitesService } from 'src/services/sites/sites.service';
import { SiteConfigurationTemplate, TagMapping, ImportResult } from './types';
import { UploadStep } from './steps/UploadStep';
import { MapTagsStep } from './steps/MapTagsStep';
import { OverviewStep } from './steps/OverviewStep';
import { ResultStep } from './steps/ResultStep';
import { validateAndParseTemplate, readFileAsJson, buildAutoMapping } from './utils';

const STEPS = ['Upload', 'Map Tags', 'Overview', 'Result'];

type ImportWizardState = {
  file: File | null;
  fileError: string | null;
  template: SiteConfigurationTemplate | null;
  tagMapping: TagMapping;
  isImporting: boolean;
  importResult: ImportResult | null;
  importError: string | null;
  confirmOpen: boolean;
};

const initialImportWizardState: ImportWizardState = {
  file: null,
  fileError: null,
  template: null,
  tagMapping: {},
  isImporting: false,
  importResult: null,
  importError: null,
  confirmOpen: false,
};

type ImportWizardAction =
  | { type: 'FILE_SELECTED'; file: File | null }
  | { type: 'FILE_PARSED'; template: SiteConfigurationTemplate }
  | { type: 'FILE_ERROR'; error: string | null }
  | { type: 'TAG_MAPPING_CHANGED'; sourceTagId: string; destinationTagId: string }
  | {
      type: 'AUTO_FILL_TAG_MAPPING';
      template: SiteConfigurationTemplate;
      destinationTags: { id: string; rawInputName: string; displayName: string | null }[];
    }
  | { type: 'IMPORT_START' }
  | { type: 'IMPORT_SUCCESS'; result: ImportResult }
  | { type: 'IMPORT_ERROR'; error: string }
  | { type: 'IMPORT_ERROR_DISMISSED' }
  | { type: 'CONFIRM_OPEN' }
  | { type: 'CONFIRM_CLOSE' }
  | { type: 'RESET' };

const importWizardReducer = (
  state: ImportWizardState,
  action: ImportWizardAction
): ImportWizardState => {
  switch (action.type) {
    case 'FILE_SELECTED':
      return { ...state, file: action.file, template: null, fileError: null };
    case 'FILE_PARSED':
      return { ...state, template: action.template, fileError: null };
    case 'FILE_ERROR':
      return { ...state, fileError: action.error };
    case 'TAG_MAPPING_CHANGED':
      return {
        ...state,
        tagMapping: { ...state.tagMapping, [action.sourceTagId]: action.destinationTagId },
      };
    case 'AUTO_FILL_TAG_MAPPING':
      // Only seed once
      if (Object.keys(state.tagMapping).length > 0) return state;
      return { ...state, tagMapping: buildAutoMapping(action.template, action.destinationTags) };
    case 'IMPORT_START':
      return { ...state, isImporting: true, importError: null };
    case 'IMPORT_SUCCESS':
      return { ...state, isImporting: false, importResult: action.result };
    case 'IMPORT_ERROR':
      return { ...state, isImporting: false, importError: action.error };
    case 'IMPORT_ERROR_DISMISSED':
      return { ...state, importError: null };
    case 'CONFIRM_OPEN':
      return { ...state, confirmOpen: true };
    case 'CONFIRM_CLOSE':
      return { ...state, confirmOpen: false };
    case 'RESET':
      return initialImportWizardState;
    default:
      return state;
  }
};

// Wizard

type ImportWizardProps = {
  open: boolean;
  onClose: () => void;
};

export const ImportWizard = ({ open, onClose }: ImportWizardProps) => {
  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const [step, setStep] = useState(0);
  const [state, dispatch] = useReducer(importWizardReducer, initialImportWizardState);
  const {
    file,
    fileError,
    template,
    tagMapping,
    isImporting,
    importResult,
    importError,
    confirmOpen,
  } = state;

  const { data: destinationTags = [], isLoading: isLoadingDestinationTags } = useSiteTags(
    customerId ?? '',
    siteId ?? '',
    step === 1 && !!customerId && !!siteId
  );

  const handleFileChange = useCallback(async (file: File | null) => {
    dispatch({ type: 'FILE_SELECTED', file: file });
    if (!file) return;

    try {
      const json = await readFileAsJson(file);
      const parsed = validateAndParseTemplate(json);
      dispatch({ type: 'FILE_PARSED', template: parsed });
    } catch (e: unknown) {
      dispatch({ type: 'FILE_ERROR', error: e instanceof Error ? e.message : 'Invalid file.' });
    }
  }, []);

  useEffect(() => {
    if (step === 1 && template && destinationTags.length > 0) {
      dispatch({ type: 'AUTO_FILL_TAG_MAPPING', template, destinationTags });
    }
  }, [step, template, destinationTags]);

  const handleClose = () => {
    setStep(0);
    dispatch({ type: 'RESET' });
    onClose();
  };

  const handleNext = useCallback(() => setStep((s) => s + 1), []);
  const handleBack = () => setStep((s) => s - 1);

  const handleTagMappingChange = (sourceTagId: string, destinationTagId: string) => {
    dispatch({ type: 'TAG_MAPPING_CHANGED', sourceTagId, destinationTagId });
  };

  const handleImport = async () => {
    if (!template || !customerId || !siteId) return;
    dispatch({ type: 'IMPORT_START' });
    try {
      const result = await SitesService.importConfigurationTemplate(
        customerId,
        siteId,
        template,
        tagMapping
      );
      dispatch({ type: 'IMPORT_SUCCESS', result });
      setStep(3);
    } catch (e: unknown) {
      const detail = axios.isAxiosError(e) ? e.response?.data?.detail : undefined;
      dispatch({
        type: 'IMPORT_ERROR',
        error:
          typeof detail === 'string' && detail
            ? detail
            : e instanceof Error
            ? e.message
            : 'Import failed. Please try again.',
      });
    }
  };

  const canAdvance = step === 0 ? !!template && !fileError : true;
  const isLastBeforeImport = step === STEPS.length - 2; // step 2 = Overview

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 600 } }}
    >
      <DialogTitle>
        <Typography variant="h6">Import Site Configuration</Typography>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={step} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {step === 0 && (
          <UploadStep
            file={file}
            onFileChange={handleFileChange}
            error={fileError}
            onError={(error) => dispatch({ type: 'FILE_ERROR', error })}
          />
        )}

        {step === 1 && template && (
          <MapTagsStep
            template={template}
            destinationTags={destinationTags}
            isLoadingDestinationTags={isLoadingDestinationTags}
            tagMapping={tagMapping}
            onTagMappingChange={handleTagMappingChange}
          />
        )}

        {step === 2 && template && <OverviewStep template={template} tagMapping={tagMapping} />}

        {step === 3 && importResult && <ResultStep result={importResult} />}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          {step === 3 ? 'Close' : 'Cancel'}
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {step > 0 && step < 3 && (
            <Button variant="outlined" onClick={handleBack} disabled={isImporting}>
              Back
            </Button>
          )}
          {step < STEPS.length - 2 && (
            <Button variant="contained" onClick={handleNext} disabled={!canAdvance}>
              Next
            </Button>
          )}
          {isLastBeforeImport && (
            <Button
              variant="contained"
              onClick={() => dispatch({ type: 'CONFIRM_OPEN' })}
              disabled={isImporting}
              startIcon={isImporting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isImporting ? 'Importing…' : 'Import Template'}
            </Button>
          )}
        </Box>
      </DialogActions>

      <Dialog
        open={confirmOpen}
        onClose={() => dispatch({ type: 'CONFIRM_CLOSE' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Import</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This is a destructive action. Advanced tag mapping, site preview, and other resources
            associated with the tags you mapped may be overridden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch({ type: 'CONFIRM_CLOSE' })}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              dispatch({ type: 'CONFIRM_CLOSE' });
              handleImport();
            }}
          >
            Confirm Import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!importError}
        onClose={() => dispatch({ type: 'IMPORT_ERROR_DISMISSED' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Failed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
            {importError}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => dispatch({ type: 'IMPORT_ERROR_DISMISSED' })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
